import { useState, useEffect } from 'react';

/**
 * Fired on window when a useTheme instance changes the theme, so every other
 * mounted instance (the Header toggle, the Account page's Theme preference)
 * follows instead of holding a stale copy.
 */
export const THEME_CHANGE_EVENT = 'saf3ai:theme-changed';

const readSavedTheme = () => {
    try {
        const saved = localStorage.getItem('theme');
        return saved === 'light' || saved === 'dark' ? saved : null;
    } catch {
        return null;
    }
};

export function useTheme() {
    const [theme, setTheme] = useState(() => {
        // Get initial theme from localStorage or default to 'dark'
        return readSavedTheme() || 'dark';
    });

    useEffect(() => {
        // Apply theme to document element (html) for CSS variables
        const htmlElement = document.documentElement;
        const bodyElement = document.body;

        if (theme === 'dark') {
            htmlElement.classList.add('dark-mode');
            bodyElement.classList.add('dark-mode');
        } else {
            htmlElement.classList.remove('dark-mode');
            bodyElement.classList.remove('dark-mode');
        }

        // Save to localStorage; tell the other instances only on a real change.
        const previous = readSavedTheme();
        try {
            localStorage.setItem('theme', theme);
        } catch { /* storage blocked — the classes above still apply */ }
        if (previous !== theme) {
            window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
        }
    }, [theme]);

    useEffect(() => {
        const sync = () => {
            const saved = readSavedTheme();
            if (saved) setTheme(saved);
        };
        window.addEventListener(THEME_CHANGE_EVENT, sync);
        return () => window.removeEventListener(THEME_CHANGE_EVENT, sync);
    }, []);

    const toggleTheme = () => {
        setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
    };

    return { theme, toggleTheme, setTheme };
}
